import * as React from 'react'
import { ReactElement } from 'react'
import { Button, makeStyles, shorthands, Text, tokens } from '@fluentui/react-components'
import { ToastContentProps } from 'react-toastify'
import useDocumentGenerationHandler, {
    DocumentGenerationContextData,
    DocumentGenerationStatusData,
} from '../../hooks/document/useDocumentGenerationHandler'
import { stealthClick } from '../StealthLink'
import useAxios from '../../hooks/useAxios'
import {
    ArrowDownloadFilled,
    ArrowDownloadRegular,
    ArrowRepeatAllFilled,
    ArrowRepeatAllRegular,
    bundleIcon,
    DocumentDismissFilled,
    DocumentDismissRegular,
} from '@fluentui/react-icons'
import ErrorPopover from './ErrorPopover'
import { QueueData, QueueStatus } from '../../hooks/useQueueStateHandler'
import { useTranslation } from 'react-i18next'

export const useDocumentToastStyles = makeStyles({
    toaster: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalS),
    },
    moreInfo: {
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr',
        ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalXS),
        alignItems: 'baseline',
    },
    details: {
        ...shorthands.overflow('auto'),
        maxHeight: '10em',
    },
    button: {
        display: 'flex',
        justifyContent: 'end',
    },
})

const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)
const RepeatIcon = bundleIcon(ArrowRepeatAllFilled, ArrowRepeatAllRegular)
const DismissIcon = bundleIcon(DocumentDismissFilled, DocumentDismissRegular)

function getDocumentToastMessage<RequestT>(
    { status, context }: QueueData<RequestT, DocumentGenerationStatusData, DocumentGenerationContextData>,
    translate: (key: string, options?: any) => string,
) {
    const type = context.generationType.toLowerCase().replaceAll(/[-_]/g, '')
    const label = translate(`toast.document.type.${type}.title`)
    const determinerLC = translate(`toast.document.type.${type}.determiner`)
    const noun = translate(`toast.document.type.${type}.noun`)
    const determinerUC = determinerLC.charAt(0).toUpperCase() + determinerLC.slice(1)

    const title = context.title || label

    return new Map<QueueStatus, ReactElement | string>([
        [
            QueueStatus.Dispatch,
            translate('toast.document.dispatch', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Queued,
            translate('toast.document.queued', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Delayed,
            translate('toast.document.delayed', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Cancelled,
            translate('toast.document.cancelled', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Failed,
            translate('toast.document.failed', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Finished,
            translate('toast.document.finished', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
        [
            QueueStatus.Duplicate,
            translate('toast.document.duplicate', {
                determinerLC: determinerLC,
                determinerUC: determinerUC,
                noun: noun,
                title: title,
            }),
        ],
    ]).get(status.current)
}

export default function DocumentToastContent<RequestT>(
    props: ToastContentProps<QueueData<RequestT, DocumentGenerationStatusData, DocumentGenerationContextData>>,
): ReactElement {
    const axios = useAxios()
    const handler = useDocumentGenerationHandler()
    const { t: translate } = useTranslation()

    const styles = useDocumentToastStyles()
    const status = props.data.status.current

    return (
        <div className={styles.toaster}>
            <Text>{getDocumentToastMessage<RequestT>(props.data, translate)}</Text>
            {props.data.status.driveItem && (
                <div className={styles.button}>
                    <Button
                        icon={<DownloadIcon />}
                        appearance="subtle"
                        onClick={() => stealthClick({ driveItem: props.data.status.driveItem, download: true }, axios)}
                    >
                        {translate('toast.download')}
                    </Button>
                </div>
            )}
            {status === QueueStatus.Duplicate && (
                <div className={styles.button}>
                    <Button
                        icon={<RepeatIcon />}
                        appearance="subtle"
                        onClick={() => {
                            handler.dispatch(props.toastProps.toastId as string, props.data.context, true)
                        }}
                    >
                        {translate('toast.restart')}
                    </Button>
                </div>
            )}
            {![QueueStatus.Cancelled, QueueStatus.Duplicate, QueueStatus.Failed, QueueStatus.Finished].includes(
                status,
            ) && (
                <div className={styles.button}>
                    <Button
                        icon={<DismissIcon />}
                        appearance="subtle"
                        onClick={() => {
                            handler.transition(props.toastProps.toastId as string, QueueStatus.Cancelled)
                        }}
                    >
                        {translate('toast.cancel')}
                    </Button>
                </div>
            )}
            <ErrorPopover error={props.data.status.error} styles={styles} />
        </div>
    )
}
